import { useState, useEffect, useCallback } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { branches } from "@/mocks/contact";
import BranchMap from "@/pages/contacto/components/BranchMap";
import DeliverySection from "@/pages/contacto/components/DeliverySection";

const CONTACT_FORM_URL = "https://readdy.ai/api/form/d7ogpopuelgcie28i39g";

export default function ContactoPage() {
  const [activeBranch, setActiveBranch] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleNearestBranch = useCallback((index: number) => {
    setActiveBranch(index);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) {
      setError("El mensaje no puede superar los 500 caracteres.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
      });
      await fetch(CONTACT_FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError("Hubo un error al enviar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative h-56 md:h-72 flex items-end pb-10 pt-20">
        <img
          src="https://static.readdy.ai/image/707e9a4f9368999593f702b6caac5116/c8a26a93758285c912b4f902a2f16339.png"
          alt="Dónde Pecar"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full">
          <span className="text-[#7ee8f4] text-xs font-semibold uppercase tracking-widest">
            Inicio / Dónde Pecar
          </span>
          <h1
            className="text-4xl md:text-5xl font-bold text-white mt-2"
          >
            Dónde Pecar
          </h1>
          <p className="text-white/80 text-sm mt-2">
            Encuentra la sucursal más cercana a ti y ven a pecar
          </p>
        </div>
      </section>

      {/* Map Section */}
      <BranchMap
        branches={branches}
        activeBranch={activeBranch}
        onSelectBranch={setActiveBranch}
        onNearestFound={handleNearestBranch}
      />

      {/* Delivery Section */}
      <DeliverySection />

      {/* Contact Form + Info */}
      <section className="py-20 bg-[#e0f7fa]/20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Form */}
            <div className="lg:w-3/5">
              <span className="text-xs font-semibold text-[#7c5cbf] uppercase tracking-widest">
                Escríbenos
              </span>
              <h2
                className="text-3xl font-bold text-[#2d1a0e] mt-2 mb-8"
              >
                Envíanos un Mensaje
              </h2>

              {submitted ? (
                <div className="bg-[#e0f7fa]/40 rounded-2xl p-10 text-center">
                  <i className="ri-check-double-line text-5xl text-[#00b4c8]"></i>
                  <h3 className="text-xl font-bold text-[#2d1a0e] mt-4 mb-2">
                    ¡Mensaje enviado!
                  </h3>
                  <p className="text-[#7a5c44] text-sm">
                    Tu mensaje fue enviado a <strong>san7opecado@gmail.com</strong>. Nos pondremos en contacto contigo muy pronto. También puedes escribirnos directamente por WhatsApp.
                  </p>
                  <a
                    href="https://wa.me/5217291317070"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 mt-6 bg-[#00b4c8] text-white font-semibold text-sm px-6 py-3 rounded-full hover:bg-[#0099aa] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-whatsapp-line"></i> Continuar en WhatsApp
                  </a>
                </div>
              ) : (
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-[#6b5b6e] mb-1.5 block">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Tu nombre"
                        className="w-full px-4 py-3 rounded-xl border border-[#b2ebf2] bg-white text-sm text-[#2d1a0e] placeholder-[#9b8aae] focus:outline-none focus:border-[#00b4c8] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#6b5b6e] mb-1.5 block">
                        Correo electrónico *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="tu@correo.com"
                        className="w-full px-4 py-3 rounded-xl border border-[#b2ebf2] bg-white text-sm text-[#2d1a0e] placeholder-[#9b8aae] focus:outline-none focus:border-[#00b4c8] transition-colors"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-[#6b5b6e] mb-1.5 block">
                        Teléfono
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+52 55 0000 0000"
                        className="w-full px-4 py-3 rounded-xl border border-[#b2ebf2] bg-white text-sm text-[#2d1a0e] placeholder-[#9b8aae] focus:outline-none focus:border-[#00b4c8] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-[#6b5b6e] mb-1.5 block">
                        Asunto *
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-[#b2ebf2] bg-white text-sm text-[#2d1a0e] focus:outline-none focus:border-[#00b4c8] transition-colors cursor-pointer"
                      >
                        <option value="">Selecciona un asunto</option>
                        <option value="Pedido personalizado">Pedido personalizado</option>
                        <option value="Información de productos">Información de productos</option>
                        <option value="Entrega a domicilio">Entrega a domicilio</option>
                        <option value="San7as Huellitas">San7as Huellitas</option>
                        <option value="Otro">Otro</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#6b5b6e] mb-1.5 flex justify-between">
                      <span>Mensaje *</span>
                      <span className={formData.message.length > 500 ? "text-[#d4607a]" : ""}>
                        {formData.message.length}/500
                      </span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="Cuéntanos en qué podemos ayudarte..."
                      className="w-full px-4 py-3 rounded-xl border border-[#b2ebf2] bg-white text-sm text-[#2d1a0e] placeholder-[#9b8aae] focus:outline-none focus:border-[#00b4c8] transition-colors resize-none"
                    ></textarea>
                  </div>

                  {error && (
                    <p className="text-[#d4607a] text-xs">{error}</p>
                  )}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-[#00b4c8] text-white font-semibold py-3.5 rounded-full hover:bg-[#0099aa] transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-60"
                    >
                      {loading ? "Enviando..." : "Enviar Mensaje"}
                    </button>
                    <a
                      href={`https://wa.me/5217291317070?text=Hola! ${encodeURIComponent(formData.message || "Me gustaría obtener más información.")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 bg-[#00b4c8] text-white font-semibold py-3.5 rounded-full hover:bg-[#0099aa] transition-colors cursor-pointer whitespace-nowrap text-sm"
                    >
                      <i className="ri-whatsapp-line"></i> Escribir por WhatsApp
                    </a>
                  </div>
                </form>
              )}
            </div>

            {/* Contact Info */}
            <div className="lg:w-2/5">
              <span className="text-xs font-semibold text-[#00b4c8] uppercase tracking-widest">
                Información
              </span>
              <h2
                className="text-3xl font-bold text-[#2d1a0e] mt-2 mb-8"
              >
                Estamos Aquí
              </h2>
              <div className="flex flex-col gap-5 mb-10">
                {[
                  { icon: "ri-phone-line", label: "Teléfono", value: "+52 1 729 131 7070", href: "tel:+5217291317070" },
                  { icon: "ri-mail-line", label: "Correo", value: "san7opecado@gmail.com", href: "mailto:san7opecado@gmail.com" },
                  { icon: "ri-time-line", label: "Horario", value: "Lun-Sáb 8:00-21:00 | Dom 9:00-20:00", href: null },
                  { icon: "ri-whatsapp-line", label: "WhatsApp", value: "+52 1 729 131 7070", href: "https://wa.me/5217291317070" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#f5f0ff] shrink-0">
                      <i className={`${item.icon} text-xl text-[#7c5cbf]`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-[#b09080] font-medium">{item.label}</p>
                      {item.href ? (
                        <a
                          href={item.href}
                          target={item.href.startsWith("http") ? "_blank" : undefined}
                          rel="noopener noreferrer"
                          className="text-sm font-semibold text-[#2d1a0e] hover:text-[#7c5cbf] transition-colors"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-sm font-semibold text-[#2d1a0e]">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {/* Social */}
              <div>
                <p className="text-xs font-semibold text-[#7a5c44] uppercase tracking-wider mb-4">
                  Síguenos
                </p>
                <div className="flex items-center gap-3">
                  {[
                    { icon: "ri-instagram-line", href: "https://www.instagram.com/san7opecado/", label: "Instagram", bg: "bg-[#f9b8c8]/30", text: "text-[#d4607a]", hoverBg: "hover:bg-[#f9b8c8]", hoverText: "hover:text-white" },
                    { icon: "ri-facebook-line", href: "https://www.facebook.com/san7opecado/", label: "Facebook", bg: "bg-[#c9b8f0]/30", text: "text-[#7c5cbf]", hoverBg: "hover:bg-[#c9b8f0]", hoverText: "hover:text-white" },
                    { icon: "ri-tiktok-line", href: "https://www.tiktok.com/@san7o.pecado", label: "TikTok", bg: "bg-[#fde68a]/40", text: "text-[#b08000]", hoverBg: "hover:bg-[#fde68a]", hoverText: "hover:text-[#2d1a0e]" },
                  ].map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      className={`w-11 h-11 flex items-center justify-center rounded-full ${social.bg} ${social.text} ${social.hoverBg} ${social.hoverText} transition-colors cursor-pointer`}
                    >
                      <i className={`${social.icon} text-lg`}></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
